import React, { useState, useEffect } from 'react'
import { X, Search, ChevronLeft, ChevronRight } from 'lucide-react'
import { toast } from 'react-toastify'
import sacMasterService from '../../services/sacMasterService'
import './SACMasterModal.css'

const SACMasterModal = ({ isOpen, onClose, onSelectSAC, selectedSACCode = null }) => {
  const [sacData, setSacData] = useState([])
  const [loading, setLoading] = useState(false)
  const [searchTerm, setSearchTerm] = useState('')
  const [selectedSAC, setSelectedSAC] = useState(null)
  const [isSaveEnabled, setIsSaveEnabled] = useState(false)
  const [isDragging, setIsDragging] = useState(false)
  const [dragOffset, setDragOffset] = useState({ x: 0, y: 0 })


  const [currentPage, setCurrentPage] = useState(1)
  const [totalPages, setTotalPages] = useState(1)
  const [totalRecords, setTotalRecords] = useState(0)
  const itemsPerPage = 10

  // Load SAC master data
  useEffect(() => {
    if (isOpen) {
      loadSACData()
    }
  }, [isOpen, currentPage, searchTerm])

  // Reset selection only when modal opens, or pre-select if selectedSACCode is provided
  useEffect(() => {
    if (isOpen) {
      if (selectedSACCode) {
        // Pre-select the SAC if selectedSACCode is provided
        const preSelectedSAC = sacData.find(sac => sac.sacCode === selectedSACCode)
        if (preSelectedSAC) {
          setSelectedSAC(preSelectedSAC)
          setIsSaveEnabled(true)
        } else {
          setSelectedSAC(null)
          setIsSaveEnabled(false)
        }
      } else {
        setSelectedSAC(null)
        setIsSaveEnabled(false)
      }
    }
  }, [isOpen, selectedSACCode, sacData])

  // Handle ESC key to close modal and drag events
  useEffect(() => {
    const handleEscKey = (event) => {
      if (event.key === 'Escape' && isOpen) {
        onClose()
      }
    }

    if (isOpen) {
      document.addEventListener('keydown', handleEscKey)
      document.addEventListener('mousemove', handleMouseMove)
      document.addEventListener('mouseup', handleMouseUp)
      // Prevent body scroll when modal is open
      document.body.style.overflow = 'hidden'
    }

    return () => {
      document.removeEventListener('keydown', handleEscKey)
      document.removeEventListener('mousemove', handleMouseMove)
      document.removeEventListener('mouseup', handleMouseUp)
      document.body.style.overflow = 'unset'
    }
  }, [isOpen, onClose, isDragging, dragOffset])

  const loadSACData = async () => {
    setLoading(true)
    try {
      const response = await sacMasterService.getAllSACs({
        page: currentPage,
        limit: itemsPerPage,
        search: searchTerm,
        status: 'Active' // Only fetch active SAC records
      })

      if (response.success) {
        setSacData(response.data || [])
        setTotalPages(response.pagination?.totalPages || 1)
        setTotalRecords(response.pagination?.total || 0)
      } else {
        toast.error(response.message || 'Failed to fetch SAC master records')
      }
    } catch (error) {
      console.error('Error loading SAC master data:', error)
      
      // Handle authentication errors specifically
      if (error.response?.status === 401) {
        toast.error('Authentication required. Please log in again.')
        // Close modal and redirect to login
        onClose()
        window.location.href = '/login'
        return
      }
      
      // Handle other errors
      if (error.response?.status === 403) {
        toast.error('You do not have permission to access SAC master data.')
      } else if (error.response?.status === 404) {
        toast.error('SAC master service not found.')
      } else {
        toast.error('Failed to load SAC master data. Please try again.')
      }
    } finally {
      setLoading(false)
    }
  }

  const handleSearch = (e) => {
    e.preventDefault()
    setCurrentPage(1)
    loadSACData()
  }

  const handleSACSelect = (sac) => {
    setSelectedSAC(sac)
    setIsSaveEnabled(true)
  }

  const handleSaveAndClose = () => {
    if (selectedSAC) {
      onSelectSAC(selectedSAC)
      onClose()
    } else {
      toast.error('Please select a SAC code')
    }
  }

  // Drag functionality
  const handleMouseDown = (e) => {
    if (e.target.closest('.sac-modal-header')) {
      setIsDragging(true)
      const rect = e.currentTarget.getBoundingClientRect()
      setDragOffset({
        x: e.clientX - rect.left,
        y: e.clientY - rect.top
      })
    }
  }

  const handleMouseMove = (e) => {
    if (isDragging) {
      const modal = document.querySelector('.sac-modal')
      if (modal) {
        modal.style.left = `${e.clientX - dragOffset.x}px`
        modal.style.top = `${e.clientY - dragOffset.y}px`
        modal.style.transform = 'none'
      }
    }
  }

  const handleMouseUp = () => {
    setIsDragging(false)
  }

  const handlePageChange = (newPage) => {
    if (newPage >= 1 && newPage <= totalPages) {
      setCurrentPage(newPage)
    }
  }

  const formatRate = (rate) => {
    return rate ? `${rate.toFixed(2)}%` : '0.00%'
  }

  if (!isOpen) return null

  const handleOverlayClick = (e) => {
    if (e.target === e.currentTarget) {
      onClose()
    }
  }

  return (
    <div className="sac-modal-overlay" onClick={handleOverlayClick}>
      <div className="sac-modal">
        {/* Header */}
        <div 
          className="sac-modal-header" 
          onMouseDown={handleMouseDown}
          style={{ cursor: isDragging ? 'grabbing' : 'grab' }}
        >
          <h2>SAC Master</h2>
          <button className="sac-modal-close" onClick={onClose}>
            <X size={20} />
          </button>
        </div>

        {/* Search and Pagination */}
        <div className="sac-modal-search-section">
          <form onSubmit={handleSearch} className="sac-search-form">
            <div className="sac-search-wrapper">
              <input
                type="text"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="sac-search-input"
                placeholder="Search SAC codes..."
              />
            </div>
          </form>
          
          <div className="sac-pagination">
            <button
              className="sac-pagination-btn"
              onClick={() => handlePageChange(currentPage - 1)}
              disabled={currentPage === 1}
            >
              <ChevronLeft size={16} />
            </button>
            <span className="sac-pagination-info">
              Page {currentPage} of {totalPages}
            </span>
            <button
              className="sac-pagination-btn"
              onClick={() => handlePageChange(currentPage + 1)}
              disabled={currentPage === totalPages}
            >
              <ChevronRight size={16} />
            </button>
          </div>
        </div>

        {/* Table */}
        <div className="sac-modal-content">
          {loading ? (
            <div className="sac-loading">
              <div className="sac-loading-spinner">Loading...</div>
            </div>
          ) : sacData.length === 0 ? (
            <div className="sac-empty-state">
              <div className="empty-icon">📋</div>
              <h3>No SAC codes found</h3>
              <p>No active SAC master records available.</p>
            </div>
          ) : (
            <div className="sac-table-container">
              <table className="sac-table">
                <thead>
                  <tr>
                    <th>SAC Code</th>
                    <th>Description of Service</th>
                    <th>GST Rate %</th>
                    <th>IGST Rate %</th>
                    <th>CGST Rate %</th>
                    <th>SGST Rate %</th>
                    <th>Select</th>
                  </tr>
                </thead>
                <tbody>
                  {sacData.map((sac) => (
                    <tr key={sac._id || sac.id}>
                      <td className="sac-code-cell">{sac.sacCode}</td>
                      <td className="sac-description-cell">{sac.description}</td>
                      <td className="sac-rate-cell">{formatRate(sac.gstRate)}</td>
                      <td className="sac-rate-cell">{formatRate(sac.igstRate)}</td>
                      <td className="sac-rate-cell">{formatRate(sac.cgstRate)}</td>
                      <td className="sac-rate-cell">{formatRate(sac.sgstRate)}</td>
                      <td className="sac-select-cell">
                        <input
                          type="radio"
                          name="selectedSAC"
                          checked={selectedSAC?.sacCode === sac.sacCode}
                          onChange={() => handleSACSelect(sac)}
                          className="sac-radio"
                        />
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
              
              {sacData.length === 0 && (
                <div className="sac-no-data">
                  No SAC master records found.
                </div>
              )}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="sac-modal-footer">
          <button className="sac-btn-secondary" onClick={onClose}>
            <ChevronLeft size={16} />
            Back
          </button>
          <button 
            className="sac-btn-primary" 
            onClick={handleSaveAndClose}
            disabled={!isSaveEnabled || !selectedSAC}
          >
            Save & Close
          </button>
        </div>
      </div>
    </div>
  )
}

export default SACMasterModal
